/**
 * Home Page Route
 * Landing page with tracking number entry and order lookup option
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Search, Package } from 'lucide-react';

export const Route = createFileRoute('/')({
  component: HomePage,
});

function HomePage() {
  const navigate = useNavigate();
  const [trackingNumber, setTrackingNumber] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (trackingNumber.trim()) {
      navigate({
        to: '/track/$trackingNumber',
        params: { trackingNumber: trackingNumber.trim() },
      });
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Hero section with dark background extending from header */}
      <div className="relative -mx-4 -mt-8 md:-mt-12 px-4 pt-10 pb-16 md:pt-16 md:pb-20 overflow-hidden"
        style={{ backgroundColor: 'var(--header-bg)' }}
      >
        {/* Journey line decorations */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 1000 400"
          fill="none"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M-50 280 C100 180, 250 350, 400 240 S600 100, 800 200 S950 340, 1100 180"
            stroke="#B9974F"
            strokeWidth="3"
            strokeLinecap="round"
            opacity={0.2}
          />
          <path
            d="M-50 320 C150 220, 300 380, 450 280 S650 140, 850 240 S1000 360, 1100 220"
            stroke="#B9974F"
            strokeWidth="2"
            strokeLinecap="round"
            opacity={0.1}
          />
          <path
            d="M-50 200 C80 130, 200 280, 350 180 S550 80, 700 160 S900 280, 1100 140"
            stroke="#004EFF"
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity={0.08}
          />
        </svg>

        <div className="relative max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 text-fathom-gold/70 mb-4">
            <Package className="w-4 h-4" />
            <span className="text-xs font-display uppercase tracking-[0.2em]">Find Your Fix</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-heading tracking-tight mb-4"
            style={{ color: 'var(--header-text)' }}
          >
            Track Your Order
          </h1>
          <p className="text-lg" style={{ color: 'var(--header-text)', opacity: 0.6 }}>
            Enter your tracking number to see the latest carrier status
          </p>
        </div>
      </div>

      {/* Gold accent bar matching header */}
      <div className="h-1 bg-gradient-to-r from-fathom-gold/60 via-fathom-gold to-fathom-gold/60 -mx-4" />

      {/* Tracking form — pulls up into the hero */}
      <div className="max-w-2xl mx-auto -mt-8 relative z-10">
        <div className="bg-card rounded-2xl shadow-2xl p-8 border border-border theme-transition">
          {/* Direct Tracking Number Entry */}
          <form onSubmit={handleSubmit} className="mb-8">
            <label
              htmlFor="trackingNumber"
              className="block text-sm font-display uppercase tracking-wider text-foreground-muted mb-3"
            >
              Tracking Number
            </label>
            <div className="flex gap-3">
              <input
                type="text"
                id="trackingNumber"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                placeholder="e.g., 1Z999AA10123456784"
                aria-describedby="trackingNumber-help"
                className="flex-1 px-4 py-3.5 border border-border rounded-lg focus:ring-2 focus:ring-fathom-gold focus:border-fathom-gold outline-none transition-colors font-mono bg-background text-foreground text-lg"
                required
              />
              <button
                type="submit"
              className="px-8 py-3.5 bg-fathom-gold text-on-fathom-gold font-display uppercase tracking-wider rounded-lg hover:bg-fathom-gold-light transition-colors flex items-center gap-2"
              >
                <Search className="w-4 h-4" />
                Track
              </button>
            </div>
            <p id="trackingNumber-help" className="mt-3 text-sm text-foreground-muted">
              Enter the tracking number from your shipping confirmation email.
            </p>
          </form>

          {/* Divider with journey line */}
          <div className="relative my-8">
            <svg viewBox="0 0 800 30" fill="none" className="w-full h-auto" aria-hidden="true">
              <path
                d="M0 15 C100 15, 140 5, 220 8 S340 25, 400 15 S500 3, 580 12 S700 22, 800 15"
                stroke="#B9974F"
                strokeWidth="2"
                strokeLinecap="round"
                opacity={0.4}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="px-4 bg-card text-foreground-muted text-sm">or</span>
            </div>
          </div>

          {/* Order Lookup */}
          <div className="text-center">
            <p className="text-foreground-muted mb-4">
              Don't have your tracking number?
            </p>
            <Link
              to="/lookup"
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-fathom-blue text-fathom-blue font-medium rounded-lg hover:bg-fathom-blue hover:text-white transition-colors"
            >
              Look Up by Order Number and Email
            </Link>
          </div>
        </div>
      </div>

      {/* Help Text */}
      <div className="mt-10 text-center text-sm text-foreground-muted max-w-2xl mx-auto">
        <p>
          Need help? Contact us at{' '}
          <a href="mailto:hello@fathomcoffee.com" className="text-fathom-blue hover:underline">
            hello@fathomcoffee.com
          </a>
        </p>
      </div>
    </div>
  );
}
